// src/components/sidebarcart/SidebarCartV2.js - Modern Collapsible Cart Drawer
import React, { useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Drawer, Button, Badge, Tooltip, message, Switch } from 'antd';
import { 
    ShoppingCartOutlined, 
    CloseOutlined, 
    PlusCircleOutlined,
    DoubleRightOutlined,
    DoubleLeftOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../../config';

// Import CartContext
import CartContext from '../../context/CartContext';
import CartSummary from './CartSummary';
import CustomProductModal from './CustomProductModal';

// Import invoice components
import InvoicePreviewModal from '../invoices/InvoicePreviewModal';
import InvoiceSuccessModal from '../invoices/InvoiceSuccessModal';

// Import invoice service
import { createInvoice, formatCartForInvoice } from '../../services/invoiceService';

const EXPORT_CART_API_URL = `${NODE_BACKEND_API_URL}/postexportcart`;

function SidebarCartV2() {
    const {
        cartDetails,
        isLoadingCart,
        subTotalPrice,
        totalAfterDiscount,
        roundedTotal,
        discountPercentage,
        setDiscountPercentage,
        clearCart,
        clientDatabaseName,
        addCustomProduct,
        getDiscountBreakdown,
        calculateCart,
        updateCustomProduct,
        removeItemFromCart,
        updateItemQuantity,
        updateItemSplitter,
        useCreditCardPrice,
        setUseCreditCardPrice
    } = useContext(CartContext);

    const [isOpen, setIsOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isLoadingExport, setIsLoadingExport] = useState(false);
    const [isInvoicePreviewOpen, setIsInvoicePreviewOpen] = useState(false);
    const [isInvoiceSuccessOpen, setIsInvoiceSuccessOpen] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
    const [isCustomProductModalOpen, setIsCustomProductModalOpen] = useState(false);

    // ── No-repeat-invoice guard ──────────────────────────────────────────────
    // Stores a fingerprint of the cart state at the moment an invoice succeeded,
    // plus the invoice number for display purposes.
    const [lastInvoicedSnapshot, setLastInvoicedSnapshot] = useState(null);

    // Build a deterministic string from sorted cart items + discount.
    // Any item-level or discount change will produce a different fingerprint.
    const currentCartFingerprint = useMemo(() => {
        const items = [...cartDetails]
            .sort((a, b) =>
                String(a.ProductId || a.id || '').localeCompare(
                    String(b.ProductId || b.id || '')
                )
            )
            .map(item => `${item.ProductId || item.id}:${item.quantity}:${item.splitter || 1}`)
            .join('|');
        return `${items}@${discountPercentage}`;
    }, [cartDetails, discountPercentage]);

    // True when the current cart matches exactly the last invoiced snapshot.
    const isAlreadyInvoiced = useMemo(
        () => lastInvoicedSnapshot !== null && currentCartFingerprint === lastInvoicedSnapshot.fingerprint,
        [lastInvoicedSnapshot, currentCartFingerprint]
    );

    const lastInvoiceNumber = lastInvoicedSnapshot?.invoiceNumber || null;
    // ────────────────────────────────────────────────────────────────────────

    const isDuttoUser = clientDatabaseName === 'DUTTO';
    // GIULIANO feature: detectar organizacion habilitada via auth user (no usar string-match legacy)
    const { user } = useAuth();
    const isCreditCardMarkupEnabled = !!user?.creditCardMarkupEnabled;

    // Cart item count
    const cartItemCount = cartDetails.length;

    // Handlers
    const handleOpen = useCallback(() => {
        setIsOpen(true);
        setIsCollapsed(false);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setTimeout(() => setIsCollapsed(true), 300);
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (isCollapsed && !isOpen) {
            setIsOpen(true);
            setIsCollapsed(false);
        }
    }, [isCollapsed, isOpen]);

    const handleRemoveItem = useCallback((productId) => {
        if (removeItemFromCart) {
            removeItemFromCart(productId);
        }
    }, [removeItemFromCart]);

    const handleQuantityChange = useCallback((productId, newQuantity) => {
        if (updateItemQuantity) {
            updateItemQuantity(productId, newQuantity);
        }
    }, [updateItemQuantity]);

    const handleSplitterChange = useCallback((productId, newSplitter) => {
        if (updateItemSplitter) {
            updateItemSplitter(productId, newSplitter);
        }
    }, [updateItemSplitter]);

    const handleExportToExcel = useCallback(async () => {
        if (cartDetails.length === 0) {
            message.warning('El carrito está vacío');
            return;
        }

        setIsLoadingExport(true);
        try {
            const formattedCartDetails = cartDetails.map(item => ({
                Marca: item.BrandName || '',
                Codigo: item.ProductCode || '',
                Descripcion: item.ProductDescription || '',
                Precio: item.RetailPrice || 0,
                Fraccion: item.splitter || 1,
                Cantidad: item.quantity || 0,
            }));

            const response = await axios.post(EXPORT_CART_API_URL, {
                cartDetails: formattedCartDetails,
                subTotalPrice: subTotalPrice || 0,
                discountPercentage: discountPercentage || 0,
                totalAfterDiscount: totalAfterDiscount || 0,
            }, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Cotizacion_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success('Excel exportado correctamente');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            message.error('Error al exportar a Excel');
        } finally {
            setIsLoadingExport(false);
        }
    }, [cartDetails, subTotalPrice, discountPercentage, totalAfterDiscount]);

    const handleGenerateInvoice = useCallback(async () => {
        if (cartDetails.length === 0) {
            message.warning('El carrito está vacío');
            return;
        }

        setIsInvoicePreviewOpen(true);
    }, [cartDetails]);

    const handleInvoiceConfirm = useCallback(async (invoiceData) => {
        // V2 flow: InvoicePreviewModal already created the invoice
        // This callback now just receives the invoice data to display
        console.log('🎉 [SidebarCartV2] Invoice creation confirmed:', invoiceData);
        
        try {
            // Close preview modal
            setIsInvoicePreviewOpen(false);
            
            // Set invoice data for success modal
            setInvoiceData(invoiceData);
            
            // Show success modal
            setIsInvoiceSuccessOpen(true);

            // ── No-repeat-invoice guard: snapshot current cart state ─────────
            // Derive the invoice number from whichever field the backend returns.
            const invNumber =
                invoiceData?.invoiceNumber ||
                invoiceData?.invoice?.numero_comprobante ||
                invoiceData?.numero_comprobante ||
                null;
            setLastInvoicedSnapshot({
                fingerprint: currentCartFingerprint,
                invoiceNumber: invNumber,
            });
            // ────────────────────────────────────────────────────────────────
            
            // Don't auto-clear cart - let user decide via "LIMPIAR CARRITO" button
            // clearCart();  // REMOVED: Cart should persist after invoice creation
            
            // Show success message
            message.success(`Factura ${invoiceData.invoiceNumber} generada exitosamente`);
        } catch (error) {
            console.error('Error handling invoice confirmation:', error);
            message.error('Error al procesar la confirmación de la factura');
        }
    }, [clearCart, currentCartFingerprint]);

    return (
        <>
            {/* Collapsed Tab - Always visible */}
            {isCollapsed && !isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        right: 0,
                        top: '20px',
                        zIndex: 999,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                    }}
                    onClick={handleOpen}
                    onMouseEnter={handleMouseEnter}
                >
                    <Tooltip title="Abrir Carrito" placement="left">
                        <Badge count={cartItemCount} offset={[-5, 5]}>
                            <div
                                style={{
                                    backgroundColor: 'rgba(59, 82, 112, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    padding: '20px 12px',
                                    borderTopLeftRadius: '12px',
                                    borderBottomLeftRadius: '12px',
                                    boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(59, 82, 112, 1)';
                                    e.currentTarget.style.padding = '20px 16px';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(59, 82, 112, 0.9)';
                                    e.currentTarget.style.padding = '20px 12px';
                                }}
                            >
                                <ShoppingCartOutlined style={{ fontSize: '24px', color: '#fff' }} />
                                <DoubleLeftOutlined style={{ fontSize: '16px', color: '#fff', opacity: 0.7 }} />
                            </div>
                        </Badge>
                    </Tooltip>
                </div>
            )}

            {/* Main Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <ShoppingCartOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            <span style={{ color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                                Carrito de Compras
                            </span>
                            <Badge count={cartItemCount} style={{ backgroundColor: '#52c41a' }} />
                        </div>
                        {isCreditCardMarkupEnabled && (
                            <Switch
                                checked={!!useCreditCardPrice}
                                onChange={(checked) => setUseCreditCardPrice(checked)}
                                checkedChildren="Precio Crédito"
                                unCheckedChildren="Precio Contado"
                                style={{ marginRight: '16px' }}
                            />
                        )}
                        <Button
                            type="primary"
                            icon={<PlusCircleOutlined />}
                            onClick={() => setIsCustomProductModalOpen(true)}
                            style={{
                                marginRight: '16px',
                                backgroundColor: '#ffa940',
                                borderColor: '#ffa940',
                                color: '#fff'
                            }}
                        >
                            Agregar Producto Personalizado
                        </Button>
                    </div>
                }
                placement="right"
                onClose={handleClose}
                open={isOpen}
                width={990}
                mask={true}
                maskClosable={true}
                rootClassName="frosted-drawer-v2"
                styles={{
                    mask: { backgroundColor: 'rgba(0, 0, 0, 0.15)' },
                    body: { 
                        padding: '16px',
                        background: 'transparent',
                        overflowY: 'auto'
                    },
                    header: { 
                        background: 'rgba(59, 82, 112, 0.9)',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        padding: '16px 24px'
                    },
                    content: {
                        background: 'rgba(84, 109, 143, 0.75)',
                        backdropFilter: 'blur(15px)',
                        WebkitBackdropFilter: 'blur(15px)'
                    },
                    wrapper: {
                        background: 'transparent'
                    }
                }}
                closeIcon={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff' }}>
                        <DoubleRightOutlined />
                        <CloseOutlined />
                    </div>
                }
            >
                {/* Cart Summary with all items */}
                <CartSummary
                    cartDetails={cartDetails}
                    subTotalPrice={subTotalPrice}
                    totalAfterDiscount={totalAfterDiscount}
                    roundedTotal={roundedTotal}
                    discountPercentage={discountPercentage}
                    setDiscountPercentage={setDiscountPercentage}
                    clearCart={clearCart}
                    handleGenerateInvoice={handleGenerateInvoice}
                    handleExportToExcel={handleExportToExcel}
                    handleOpenPrintPage={() => {}}
                    isLoadingExport={isLoadingExport}
                    isLoadingInvoice={isGeneratingInvoice}
                    isLoadingCart={isLoadingCart}
                    getDiscountBreakdown={getDiscountBreakdown}
                    calculateCart={calculateCart}
                    onRemoveItem={handleRemoveItem}
                    onUpdateQuantity={handleQuantityChange}
                    onUpdateSplitter={handleSplitterChange}
                    isAlreadyInvoiced={isAlreadyInvoiced}
                    lastInvoiceNumber={lastInvoiceNumber}
                />
            </Drawer>

            {/* Custom Product Modal */}
            <CustomProductModal
                open={isCustomProductModalOpen}
                onClose={() => setIsCustomProductModalOpen(false)}
                onAdd={(product) => {
                    addCustomProduct(product);
                    message.success('Producto personalizado agregado');
                }}
            />

            {/* Invoice Preview Modal */}
            <InvoicePreviewModal
                open={isInvoicePreviewOpen}
                onClose={() => setIsInvoicePreviewOpen(false)}
                cartData={{
                    cartDetails,
                    subTotalPrice,
                    totalAfterDiscount,
                    discountPercentage
                }}
                onConfirmGeneration={handleInvoiceConfirm}
                onClearCart={clearCart}
                isAlreadyInvoiced={isAlreadyInvoiced}
                lastInvoiceNumber={lastInvoiceNumber}
            />

            {/* Invoice Success Modal */}
            <InvoiceSuccessModal
                open={isInvoiceSuccessOpen}
                onClose={() => {
                    setIsInvoiceSuccessOpen(false);
                    setInvoiceData(null);
                }}
                invoiceData={invoiceData}
                cartItems={cartDetails}
                onClearCart={() => {
                    // Clear snapshot so the button reverts to normal state
                    setLastInvoicedSnapshot(null);
                    clearCart();
                }}
                onKeepCart={() => {
                    // Just close the modal, keep the cart.
                    // The snapshot already set in handleInvoiceConfirm keeps
                    // isAlreadyInvoiced = true until the cart is modified.
                    setIsInvoiceSuccessOpen(false);
                    setInvoiceData(null);
                }}
            />
        </>
    );
}

export default SidebarCartV2;
